import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  siret: '',
  notes: '',
};

const Suppliers = () => {
  const confirm = useConfirm();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/suppliers`, { withCredentials: true });
      setSuppliers(r.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/suppliers/${editing.id}`, formData, { withCredentials: true });
        toast.success('Fournisseur mis à jour');
      } else {
        await axios.post(`${API_URL}/api/suppliers`, formData, { withCredentials: true });
        toast.success('Fournisseur créé');
      }
      setShowDialog(false);
      resetForm();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/suppliers/${id}`, { withCredentials: true });
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => { setFormData(emptyForm); setEditing(null); };

  const openEditDialog = (s) => {
    setEditing(s);
    setFormData({
      name: s.name || '',
      contact_name: s.contact_name || '',
      email: s.email || '',
      phone: s.phone || '',
      address: s.address || '',
      siret: s.siret || '',
      notes: s.notes || '',
    });
    setShowDialog(true);
  };

  const filtered = suppliers.filter(s => !searchQuery ||
    (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.phone || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="suppliers-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Fournisseurs</h1>
            <p className="text-slate-600">Gérez votre base de fournisseurs</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="create-supplier-btn" className="bg-orange-500 hover:bg-orange-400 text-white">
            <Plus size={20} weight="bold" className="mr-2" /> Ajouter un fournisseur
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom, contact, email ou téléphone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-suppliers-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Contact</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Email</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Téléphone</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">SIRET</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{s.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.contact_name || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.email || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.phone || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.siret || '-'}</td>
                        <td className="py-3 px-4 text-right">
                          <button onClick={() => openEditDialog(s)} data-testid={`edit-supplier-${s.id}`} className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-2">
                            <PencilSimple size={18} />
                          </button>
                          <button onClick={() => handleDelete(s.id)} data-testid={`delete-supplier-${s.id}`} className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md">
                            <Trash size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="6" className="py-12 text-center text-slate-500">Aucun fournisseur enregistré</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="supplier-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Ajouter'} un fournisseur</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom du fournisseur *</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="supplier-name-input" required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Personne de contact</label>
                  <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} data-testid="supplier-contact-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SIRET</label>
                  <Input value={formData.siret} onChange={(e) => setFormData({ ...formData, siret: e.target.value })} data-testid="supplier-siret-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="supplier-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="supplier-phone-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="supplier-address-input"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="supplier-notes-input"
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Conditions de paiement, délais, remises…"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-supplier-btn">Annuler</Button>
                <Button type="submit" data-testid="submit-supplier-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Suppliers;
